import type { AxiosError } from 'axios'
import type { HttpClient } from '../http/HttpClient'
import {
  MASTER_DATA_UNIFICATION_ERROR_CODES,
  MasterDataUnificationError,
} from '@/domain/masterData/MasterDataUnification'
import type {
  MasterDataRecord,
  MasterDataUnificationConflict,
  MasterDataUnificationErrorCode,
  MasterDataUnificationImpactItem,
  MasterDataUnificationPreview,
  MasterDataUnificationResult,
  MasterDataUnificationSummary,
  MasterType,
  PreviewMasterDataUnificationRequest,
} from '@/domain/masterData/MasterDataUnification'
import type { MasterDataUnificationRepository } from '@/domain/masterData/MasterDataUnificationRepository'

interface RawMasterRecord {
  id: number
  name: string
  code: string | null
  erp_id: string | null
}

interface RawSummary {
  records_absorbed: number
  references_updated: number
  references_deduplicated: number
  subcategories_reassigned: number
  subcategories_auto_unified: number
}

interface RawImpactItem {
  key: string
  label: string
  count: number
  deduplicated: number
}

interface RawConflict {
  code: string
  message: string
  entity: string
  row_ids: number[]
  blocking: boolean
}

interface RawPreview {
  preview_token: string
  expires_at: string
  master_type: MasterType
  target: RawMasterRecord
  sources: RawMasterRecord[]
  summary: RawSummary
  impact: RawImpactItem[]
  conflicts: RawConflict[]
  can_execute: boolean
  notice?: string
}

interface RawResult {
  target: RawMasterRecord
  summary: RawSummary
  impact: RawImpactItem[]
}

interface RawError {
  code?: unknown
  detail?: unknown
  preview?: RawPreview
}

function toRecord(raw: RawMasterRecord): MasterDataRecord {
  return {
    id: raw.id,
    name: raw.name,
    code: raw.code,
    erpId: raw.erp_id,
  }
}

function toSummary(raw: RawSummary): MasterDataUnificationSummary {
  return {
    recordsAbsorbed: raw.records_absorbed,
    referencesUpdated: raw.references_updated,
    referencesDeduplicated: raw.references_deduplicated,
    subcategoriesReassigned: raw.subcategories_reassigned,
    subcategoriesAutoUnified: raw.subcategories_auto_unified,
  }
}

function toImpact(raw: RawImpactItem): MasterDataUnificationImpactItem {
  return {
    key: raw.key,
    label: raw.label,
    count: raw.count,
    deduplicated: raw.deduplicated,
  }
}

function toConflict(raw: RawConflict): MasterDataUnificationConflict {
  return {
    code: raw.code,
    message: raw.message,
    entity: raw.entity,
    rowIds: raw.row_ids,
    blocking: raw.blocking,
  }
}

function toPreview(raw: RawPreview): MasterDataUnificationPreview {
  return {
    previewToken: raw.preview_token,
    expiresAt: raw.expires_at,
    masterType: raw.master_type,
    target: toRecord(raw.target),
    sources: raw.sources.map(toRecord),
    summary: toSummary(raw.summary),
    impact: raw.impact.map(toImpact),
    conflicts: raw.conflicts.map(toConflict),
    canExecute: raw.can_execute,
    notice: raw.notice ?? null,
  }
}

function toResult(raw: RawResult): MasterDataUnificationResult {
  return {
    target: toRecord(raw.target),
    summary: toSummary(raw.summary),
    impact: raw.impact.map(toImpact),
  }
}

function isKnownCode(value: unknown): value is MasterDataUnificationErrorCode {
  return typeof value === 'string'
    && (MASTER_DATA_UNIFICATION_ERROR_CODES as readonly string[]).includes(value)
}

function toDomainError(error: unknown): MasterDataUnificationError {
  const raw = (error as AxiosError<RawError>)?.response?.data
  const code = isKnownCode(raw?.code) ? raw.code : 'UNKNOWN'
  const detail = typeof raw?.detail === 'string' && raw.detail.trim()
    ? raw.detail
    : ''
  return new MasterDataUnificationError(
    code,
    detail,
    raw?.preview ? toPreview(raw.preview) : null,
  )
}

export class HttpMasterDataUnificationRepository implements MasterDataUnificationRepository {
  constructor(private http: HttpClient) {}

  async preview(request: PreviewMasterDataUnificationRequest): Promise<MasterDataUnificationPreview> {
    try {
      const raw = await this.http
        .url('accountability/master-unifications/preview')
        .body({
          master_type: request.masterType,
          selected_ids: request.selectedIds,
          target_id: request.targetId,
        })
        .post<RawPreview>()
      return toPreview(raw)
    } catch (error) {
      throw toDomainError(error)
    }
  }

  async execute(previewToken: string): Promise<MasterDataUnificationResult> {
    try {
      const raw = await this.http
        .url('accountability/master-unifications/execute')
        .body({ preview_token: previewToken })
        .post<RawResult>()
      return toResult(raw)
    } catch (error) {
      throw toDomainError(error)
    }
  }
}
